/**
 * This will be the common header component for all the pages
 */

import React, { useMemo, useState } from "react";
import { Box, CssBaseline } from "@mui/material";
import AccountControlComponent from "../../components/account-control";
import { isMobileDevice } from "../../helpers/device-info-helper";
import { useParams } from "react-router-dom";
import { CustomTooltip } from "../../components/reusable-ui/material-ui-comp/tooltip";
import history from "../../history";
import { getFilteredTooltipOptions } from "../../helpers/common-helper";
import { isSourcePos } from "../../helpers/common-helper";
import { DOWNLOADS_PAGE_ROUTE, REPORTS_PAGE_ROUTE } from "../../data/routes-constants";
import { useSelector } from "react-redux";
import SwitchComponent from "../../components/reusable-ui/switch";
import './index.scss'
import { Feature, Module } from "../../data/user-features-constants";
import { Role } from "../../data/constants";
import { DOTPE_CDN_URL } from "../../config";
import { toggleChatBot } from "../../redux/slices";
import { useDispatch } from "react-redux";
import { isBetaDomain } from "../../helpers/env-helper";
import { isProdEnv } from "../../data/env-constants";

export type SubTabsDataType = {
  tabId: string;
  text: string;
  feature?: Feature | Feature[];
  isShow?: boolean;
  permission?: Role[];
  emailIds?: string[];
  licences?: string[];
  conditionsCheck?: { licence: string[]; role?: Role[]; ga_event?: string; feature?: Feature; module?: Module };
  //use this data to check access after opening page
  businessNames?: string[];
  isNew?: boolean;
  visibilityCheck?: (user: any) => Promise<boolean> | boolean;
}

interface Props {
  headingText: string;
  showBackBtn?: boolean;
  onBackBtnClick?: any;
  subTabsMap?: Record<string, SubTabsDataType>;
  renderSwitch?: boolean
  handleSwitchCheck?: (val: boolean) => void
  customLeftContent?: React.ReactNode;
  customRightContent?: React.ReactNode;
  hideDefaultRightContent?: boolean;
}

const HeaderComponent = (props: Props) => {
  const { renderSwitch = false, handleSwitchCheck } = props
  const { subTabId = "" } = useParams();
  let filteredSubTabMap = getFilteredTooltipOptions(props.subTabsMap || {}),
    subTabList = filteredSubTabMap
      ? Object.values(getFilteredTooltipOptions(filteredSubTabMap || {}))
      : [];
  const settlementId = useSelector((state: any) => state.businessReducer.midDetail || "");
  const [isSwitchChecked, setIsSwitchChecked] = useState(false);
  const recommenderConfig = useSelector((state: any) => state.recommenderReducer?.recommenderConfig);
  const showChatBotIcon = useMemo(() => {
    return recommenderConfig?.enablePrismAI && (!isProdEnv || isBetaDomain);
  }, [recommenderConfig?.enablePrismAI])
  const dispatch = useDispatch();
  function handleRistaAIClick() {
    dispatch(toggleChatBot());
  }

  function renderTooltipTitle() {
    function handleSwitchTab(id: string) {
      if (subTabId !== id)
        history.push(id);
    }
    return (
      <>
        {subTabList.map((tabItem: any, index) => {
          return (
            <p
              className={`text-15 text-[#666666] font-semibold cursor-pointer hover:text-[#0074FB]
              ${index > 0 ? "border-t border-[#EBEBEB] pt-8 mt-8" : ""}
            `}
              key={index}
              onClick={() => handleSwitchTab(tabItem.tabId)}
            >
              {tabItem?.text}
            </p>
          );
        })}
      </>
    );
  }

  return (
    <Box className={`page-heading-component flex items-center justify-between z-[12] ${isSourcePos() ? "left-[0px] sm:top-0 w-full" : ""}`}>
      <CssBaseline />
      <div className="flex items-center overflow-auto hide-scrollbar sm:justify-between sm:w-[100%]">
        {props.customLeftContent ? (
          props.customLeftContent
        ) : (
          <>
            {props.showBackBtn && (
              <div
                className="rista-sprite ic-arrow-left"
                onClick={props.onBackBtnClick}
              ></div>
            )}
            <h1
              className={`text-24 sm:text-[16px] font-semibold ${filteredSubTabMap?.[subTabId] ? "opacity-50" : ""
                }`}
            >
              {props.headingText}
            </h1>
            {renderSwitch &&
              <div className={`${isSwitchChecked ? 'bg-[#3BA045]' : 'bg-[#F2F2F2]'}  px-[12px] py-[8px] rounded-[8px] flex items-center gap-[10px] ml-[20px]`}>
                {
                  isSwitchChecked && <div className="bg-[#9BCFA0] h-[15px] w-[15px] rounded-[50%] flex items-center">
                    <div className="bg-[#FFFFFF] h-[7px] w-[7px] rounded-[50%] ml-[3.5px] mb-[.5px] text-center">
                    </div>
                  </div>
                }
                <span className={`text-[13px] ${isSwitchChecked ? 'text-[#FFFFFF]' : 'text-[#000000B2]'}   font-[600]`}>{'Live Data'}</span>
                <SwitchComponent
                  isChecked={isSwitchChecked}
                  inputProps={{ "aria-label": "ant design" }}
                  onSwitchClick={() => {
                    handleSwitchCheck?.(!isSwitchChecked);
                    setIsSwitchChecked(prev => !prev)
                  }
                  }
                  customClass={isSwitchChecked ? 'switch-class' : 'switch-class-for-notselect'}
                />
              </div>
            }
            {filteredSubTabMap?.[subTabId] ? (
              <>
                <div className="rista-sprite ic-arrow-right opacity-30 shrink-0"></div>
                <h2 className="text-24 sm:text-[16px] font-bold">
                  {filteredSubTabMap[subTabId]?.text}
                </h2>
                {!isSourcePos() && !(settlementId.length) && subTabList?.length && subTabList?.length > 1 ? (
                  <CustomTooltip
                    title={renderTooltipTitle()}
                    enterTouchDelay={0}
                    leaveTouchDelay={3000}
                    arrow
                    placement={"bottom"}
                    sx={{ minWidth: 200 }}
                  >
                    <div
                      className={`rista-sprite ic-dropdown-circle-blue ml-16 shrink-0 cursor-pointer`}
                    ></div>
                  </CustomTooltip>
                ) : null}
              </>
            ) : null}
          </>
        )}
      </div>
      <div className="flex items-center">
        {props.customRightContent ? props.customRightContent : null}
        {!props.hideDefaultRightContent && showChatBotIcon ? (
          <div
            className="bg-[linear-gradient(273.46deg,#FFFFFF_1.62%,#F2ECF6_95.69%)] rounded-[24px] border border-[#8653AA] px-16 h-38 flex items-center gap-4 cursor-pointer"
            onClick={handleRistaAIClick}
          >
            <img src={DOTPE_CDN_URL + "/ai_icon_16_16.svg"} alt="" />
            <div className="text-14 text-black">Rista AI</div>
          </div>
        ) : null}
        {!props.hideDefaultRightContent && (isMobileDevice() || isSourcePos() ? null : <AccountControlComponent />)}
      </div>
      {
        isSourcePos() && props.headingText === "Reports" ?
          <div
            className="absolute right-20 rista-sprite ic-download cursor-pointer"
            onClick={() => history.push({ pathname: DOWNLOADS_PAGE_ROUTE })}
            title="Downloads"
          ></div> : null
      }
      {
        isSourcePos() && props.headingText === "Downloads" ?
          <div
            className="absolute right-20 rista-sprite ic-reports cursor-pointer"
            onClick={() => history.push({ pathname: REPORTS_PAGE_ROUTE })}
            title="Reports"
          ></div> : null
      }
    </Box>
  );
};

export default HeaderComponent;
